import React, { useMemo } from 'react'
import { ComposedChart, Tooltip, Legend, Line, Bar, XAxis, YAxis, CartesianGrid } from 'recharts'
import { convertMs } from 'src/fixtures/utils/time'
import { CustomTooltip, PayloadItem } from './CustomTooltip'
import { Document } from '@noco/http-client/lib/noco'

export type DataItem = {
  name: string
  vt: number
  pv: number
}

type Props = {
  data?: DataItem[]
  document?: Document
}

export const ChartViewsAndAverageViewingTimePerPage = ({ data = [], document }: Props) => {
  const calculateMaxAndTicks = (values: DataItem[], extractor: (item: DataItem) => number) => {
    const max = Math.max(...values.map(extractor))
    const safeMax = max <= 0 ? 1 : max
    // 最大値を4の倍数に切り上げることで、目盛りが整数になるよう調整
    const adjustedMax = Math.ceil(safeMax / 4) * 4
    const ticks = [0, adjustedMax * 0.25, adjustedMax * 0.5, adjustedMax * 0.75, adjustedMax]
    return { max: adjustedMax, ticks }
  }

  const { max: adjustedPageViewMax, ticks: pageViewTicks } = useMemo(
    () => calculateMaxAndTicks(data, d => d.pv || 0),
    [data]
  )

  const { max: adjustedViewTimeMax, ticks: viewTimeTicks } = useMemo(
    () => calculateMaxAndTicks(data, d => d.vt || 0),
    [data]
  )

  return (
    <ComposedChart
      width={640}
      height={470}
      data={data}
      margin={{
        top: 5,
        right: -10,
        bottom: 0,
        left: 18
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" scale="auto" />
      <YAxis
        yAxisId="viewTime"
        type="number"
        dataKey="vt"
        name="weight"
        domain={[0, adjustedViewTimeMax]}
        ticks={viewTimeTicks}
        tickFormatter={value => convertMs(value)}
      />
      <YAxis
        yAxisId="pageViews"
        type="number"
        dataKey="pv"
        name="weight"
        orientation="right"
        domain={[0, adjustedPageViewMax]}
        ticks={pageViewTicks}
        allowDecimals={false}
        tickFormatter={value => `${value}回`}
      />
      <Tooltip
        content={props => (
          <CustomTooltip
            {...props}
            payload={props.payload as PayloadItem[] | undefined}
            slideImages={document?.currentSlide?.slideImages || []}
          />
        )}
        isAnimationActive={false}
      />
      <Legend />
      {/* TODO: 正式版以降 @toshi1127 色を定数化 */}
      <Bar yAxisId="viewTime" name="閲覧時間" dataKey="vt" barSize={10} fill="#FFA216" />
      <Line yAxisId="pageViews" name="閲覧数" type="monotone" dataKey="pv" stroke="#F153A9" />
    </ComposedChart>
  )
}
